import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { Camera, CheckCircle2, FileText, Languages, Phone, Store, UserRound } from "lucide-react";
import type { DocumentDto, UserDto } from "@gatiq/shared";
import { Alert, Badge, Button, Card, EmptyState, Skeleton, statusTone } from "@/ui";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { KarigarShell } from "@/components/KarigarShell";
import { LedgerList } from "@/components/LedgerList";
import { PointsBalanceCard } from "@/components/PointsBalanceCard";
import { fetchPoints, type LedgerRow } from "@/lib/karigarPoints";
import { useLanguage } from "@/i18n/language";
import { LanguageSwitch } from "@/i18n/LanguageSwitch";

/** Enough to see the last scan or two without duplicating the History tab. */
const RECENT_COUNT = 3;

const UNDER_REVIEW = { hi: "समीक्षा में", en: "Under review" };
const KYC_LABEL: Record<string, { hi: string; en: string }> = {
  approved: { hi: "स्वीकृत", en: "Approved" },
  pending_counter: UNDER_REVIEW,
  pending_sales_rep: UNDER_REVIEW,
  pending_org_admin: UNDER_REVIEW,
  rejected: { hi: "अस्वीकृत", en: "Rejected" },
};

/**
 * Every line is in the karigar's chosen language — Hindi until they pick
 * (D-11), switchable from the profile button in the header.
 *
 * Scanning works before KYC approval; withdrawing does not. That is the
 * product owner's own rule, and it only holds together if the karigar is told
 * so *before* they build up a balance they cannot touch — hence the notice on
 * the balance itself rather than a refusal later at the counter.
 */
export function KarigarHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, pick } = useLanguage("hi");
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [profile, setProfile] = useState<UserDto | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [recent, setRecent] = useState<LedgerRow[]>([]);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<UserDto>("/org/me/karigar")
      .then((res) => {
        if (!cancelled) setProfile(res);
      })
      .catch(() => undefined);

    /*
     * Balance and the last few rows arrive together — the endpoint returns a
     * page of the ledger alongside the total, so showing recent activity here
     * costs one extra row of payload rather than a second request.
     */
    fetchPoints(1, RECENT_COUNT)
      .then((res) => {
        if (cancelled) return;
        setBalance(res.balance);
        setRecent(res.items);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoadingBalance(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Documents the org has published for the field. A failed read is silent on
   * the home screen — the next "Open documents" tap will retry — but we never
   * want a missing catalogue to look like a layout bug.
   */
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ items: DocumentDto[] }>("/org/me/documents")
      .then((res) => {
        if (!cancelled) {
          setDocuments(res.items);
          setLoadingDocuments(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadingDocuments(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = user?.name.split(/\s+/)[0] ?? "";
  const approved = profile?.kycStatus === "approved";

  return (
    <KarigarShell active="home" title={t(`नमस्ते, ${firstName}`, `Hi, ${firstName}`)}>
      <PointsBalanceCard
        balance={balance}
        loading={loadingBalance}
        note={
          profile && !approved ? (
            <p className="mt-3 max-w-[34ch] text-xs text-muted">
              {t(
                "अभी स्कैन कर सकते हैं। मंज़ूरी के बाद QCoin रिडीम कर सकेंगे।",
                "You can scan now. You can redeem QCoin once you are approved.",
              )}
            </p>
          ) : null
        }
      />

      <Button
        block
        icon={<Camera aria-hidden="true" className="size-5" />}
        onClick={() => orgSlug && navigate(`/${orgSlug}/karigar/scan`)}
        size="lg"
      >
        {t("कोड स्कैन करें", "Scan a code")}
      </Button>

      {profile?.kycStatus === "rejected" ? (
        <Alert kind="warning" title={t("आपका आवेदन अस्वीकृत हुआ", "Your application was rejected")}>
          {t("अपने काउंटर से बात करें।", "Speak to your counter.")}
        </Alert>
      ) : null}

      <Card>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <IconChip>
            <Store className="size-[1.125rem]" />
          </IconChip>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted">{t("आपका काउंटर", "Your counter")}</p>
            {profile ? (
              <p className="truncate font-title text-[15px] font-semibold text-ink">
                {profile.counterName ?? "—"}
              </p>
            ) : (
              <Skeleton className="mt-1 h-5 w-2/3" />
            )}
          </div>
        </div>
      </Card>

      <Card>
        <button
          className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
          onClick={() => orgSlug && navigate(`/${orgSlug}/karigar/documents`)}
          type="button"
        >
          <IconChip>
            <FileText className="size-[1.125rem]" />
          </IconChip>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted">{t("कैटलॉग और रेट लिस्ट", "Catalogues & rate lists")}</p>
            <p className="truncate font-title text-[15px] font-semibold text-ink">
              {loadingDocuments ? (
                <Skeleton className="mt-1 h-5 w-2/3" />
              ) : documents.length > 0 ? (
                t(`${documents.length} दस्तावेज़`, `${documents.length} document${documents.length > 1 ? "s" : ""}`)
              ) : (
                t("कोई नहीं", "None yet")
              )}
            </p>
          </div>
        </button>
      </Card>

      <Card>
        <p className="border-b border-rule/70 px-4 pt-3.5 pb-3 font-title text-sm font-semibold text-ink">
          {t("हाल की गतिविधि", "Recent activity")}
        </p>

        {loadingBalance ? (
          <div className="flex flex-col gap-3 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : recent.length > 0 ? (
          <>
            <LedgerList rows={recent} />
            {/* Its own row rather than a corner link: two languages will not
                fit beside the heading without wrapping into it. */}
            <Button
              block
              className="rounded-t-none border-t border-rule/70"
              onClick={() => orgSlug && navigate(`/${orgSlug}/karigar/history`)}
              variant="ghost"
            >
              {t("सब देखें", "See all")}
            </Button>
          </>
        ) : (
          <EmptyState
            message={t("कोड स्कैन करते ही यहाँ दिखने लगेगा।", "Scan a code and it will appear here.")}
            title={t("अभी कुछ नहीं है", "Nothing yet")}
          />
        )}
      </Card>

      <div id="karigar-profile">
        <Card>
          <p className="border-b border-rule/70 px-4 pt-3.5 pb-3 font-title text-sm font-semibold text-ink">
            {t("प्रोफ़ाइल", "Profile")}
          </p>
          <div className="flex flex-col divide-y divide-rule/70">
            <ProfileRow icon={<UserRound className="size-[1.125rem]" />}>
              {user?.name}
              {user?.displayId ? <span className="ml-1.5 font-mono text-xs text-muted">{user.displayId}</span> : null}
            </ProfileRow>
            <ProfileRow icon={<Phone className="size-[1.125rem]" />}>{user?.phone}</ProfileRow>
            <ProfileRow icon={<CheckCircle2 className="size-[1.125rem]" />}>
              {profile ? (
                <Badge tone={statusTone(profile.kycStatus ?? "")}>
                  {KYC_LABEL[profile.kycStatus ?? ""] ? pick(KYC_LABEL[profile.kycStatus ?? ""]!) : profile.kycStatus}
                </Badge>
              ) : (
                <Skeleton className="h-5 w-1/2" />
              )}
            </ProfileRow>
            <ProfileRow icon={<Languages className="size-[1.125rem]" />}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>{t("भाषा", "Language")}</span>
                <LanguageSwitch fallback="hi" />
              </div>
            </ProfileRow>
          </div>
        </Card>
      </div>
    </KarigarShell>
  );
}

function IconChip({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-light text-brand-ink">
      {children}
    </span>
  );
}

function ProfileRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span aria-hidden="true" className="grid size-8 shrink-0 place-items-center rounded-full bg-ink/[0.05] text-muted">
        {icon}
      </span>
      <div className="min-w-0 flex-1 text-sm text-ink">{children}</div>
    </div>
  );
}
